// src/components/Auth/LoginComponent.tsx
import { useEffect, useState, type FormEvent } from 'react';
import {
  signIn,
  fetchUserAttributes,
  fetchAuthSession,
  type SignInOutput,
} from 'aws-amplify/auth';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import './Auth.css';
import { useUserContext } from '../Context/UserContext';

interface SignInFormElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
}
interface SignInForm extends HTMLFormElement {
  readonly elements: SignInFormElements;
}

const DEFAULT_NEXT = '/';

function sanitizeNext(next?: string | null): string {
  const fallback = DEFAULT_NEXT;
  if (!next) return fallback;
  try {
    if (!next.startsWith('/')) return fallback;
    const lower = next.toLowerCase();
    if (
      lower.startsWith('/login') ||
      lower.startsWith('/register') ||
      lower.startsWith('/forgot-password') ||
      lower.startsWith('/assets') ||
      lower.startsWith('/static') ||
      lower.startsWith('/build') ||
      lower.startsWith('/vite') ||
      lower.endsWith('.js') || lower.endsWith('.css') || lower.endsWith('.map')
    ) return fallback;
    if (lower === '/') return '/';
    return next;
  } catch {
    return fallback;
  }
}

function resolveNext(location: ReturnType<typeof useLocation>): string {
  const qp = new URLSearchParams(location.search);
  const nextFromQuery = qp.get('next') || '';

  const roleRaw = qp.get('role') || qp.get('r') || '';
  const idRaw = qp.get('id') || qp.get('sessionId') || qp.get('sid') || '';
  let derived = '';
  if (idRaw && roleRaw) {
    const role = roleRaw.trim().toLowerCase();
    const id = encodeURIComponent(idRaw.trim());
    if (role.startsWith('coll')) derived = `/coll/${id}`;
    else if (role.startsWith('init')) derived = `/session/${id}`;
  }

  const st: any = location.state || {};
  const nextFromStateNext = typeof st.next === 'string' ? st.next : '';
  const nextFromStateFrom = st?.from
    ? `${st.from.pathname ?? ''}${st.from.search ?? ''}${st.from.hash ?? ''}`
    : '';

  let lsNext = '';
  try { lsNext = localStorage.getItem('nextAfterAuth') || ''; } catch {}

  const preferredRaw = nextFromQuery || derived || nextFromStateNext || nextFromStateFrom || lsNext;
  const chosen = sanitizeNext(preferredRaw) || DEFAULT_NEXT;

  try {
    if (preferredRaw) localStorage.setItem('nextAfterAuth', chosen);
    else localStorage.removeItem('nextAfterAuth');
  } catch {}

  console.debug('[Login] resolveNext:', {
    nextFromQuery, roleRaw, idRaw, derived, nextFromStateNext, nextFromStateFrom, lsNext, chosen
  });

  return chosen;
}

export default function LoginComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useUserContext();

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const next = resolveNext(location);

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchAuthSession();
        if (s?.tokens?.idToken || s?.tokens?.accessToken) {
          try {
            const attrs = await fetchUserAttributes().catch(() => ({} as any));
            const em = (attrs as any)?.email || (attrs as any)?.preferred_username || '';
            if (em && em.includes('@')) localStorage.setItem('email', em);
          } catch {}
          await refreshUser();
          navigate(next, { replace: true });
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function friendlyError(err: any): string {
    const name = err?.name || '';
    const msg = err?.message || '';
    switch (name) {
      case 'UserNotFoundException':
        return 'No account exists with that email.';
      case 'NotAuthorizedException':
        return 'Incorrect email or password.';
      case 'UserNotConfirmedException':
        return 'Please confirm your email before signing in.';
      case 'PasswordResetRequiredException':
        return 'A password reset is required. Use “Forgot Password”.';
      default:
        return msg || 'Sign in failed. Please try again.';
    }
  }

  async function handleSubmit(event: FormEvent<SignInForm>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    const form = event.currentTarget;
    const email = form.elements.email.value.trim();
    const password = form.elements.password.value;

    try {
      console.log('[Login] signIn ->', email);
      const result: SignInOutput = await signIn({ username: email, password });
      console.log('[Login] signIn result', result);

      if (!result.isSignedIn) {
        const step = result.nextStep?.signInStep;
        console.warn('[Login] not signed in, next step:', step, result.nextStep);

        if (step === 'CONFIRM_SIGN_UP') {
          setError('Please confirm your email. We can resend the code from the Register page.');
        } else if (
          step === 'RESET_PASSWORD' ||
          step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
        ) {
          setError('A new password is required. Use “Forgot Password”.');
        } else if (
          step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
          step === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
          step === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
          step === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION' ||
          step === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
        ) {
          setError('Additional verification is required to complete sign-in.');
        } else {
          setError('Sign-in did not complete. Please try again.');
        }
        return;
      }

      const s = await fetchAuthSession();
      console.log('[Login] session after signIn', {
        hasIdToken: !!s?.tokens?.idToken,
        hasAccessToken: !!s?.tokens?.accessToken,
        email: s?.tokens?.idToken?.payload?.email,
        exp: s?.tokens?.idToken?.payload?.exp,
      });

      if (!s?.tokens?.idToken && !s?.tokens?.accessToken) {
        setError('Sign-in did not complete. Please try again.');
        return;
      }

      try { await fetchUserAttributes(); } catch {}

      await refreshUser();
      navigate(next, { replace: true });
    } catch (err: any) {
      console.error('[Login] signIn error', err);
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-container">
      <h2>Sign In</h2>
      <form onSubmit={handleSubmit} autoComplete="on">
        <input name="email" type="email" placeholder="Email" autoComplete="username" required />
        <input name="password" type="password" placeholder="Password" autoComplete="current-password" required />
        <button type="submit" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign In'}
        </button>
        {error && <div className="error">{error}</div>}
      </form>
      <div className="auth-footer">
        <Link to={next ? `/register?next=${encodeURIComponent(next)}` : '/register'}>Register</Link>{' '}
        | <Link to="/forgot-password">Forgot Password?</Link>
      </div>
    </div>
  );
}
